// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.511Z
// Collection: users/$userId/purchases/$wallpaperId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Tracks which wallpapers a user has purchased, forming their personal wallpaper library. Each document represents one purchased wallpaper.
 */

export interface UsersPurchasesRequest {
  wallpaperId: string;
  purchasedAt: number | TimeOperation | IncrementOperation | TokenAmount;
  owner: AddressType;
}

export interface UsersPurchasesResponse {
  wallpaperId: string;
  purchasedAt: number;
  owner: string;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a UsersPurchases operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUsersPurchases(userId: string, wallpaperId: string, data?: UsersPurchasesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `users/${userId}/purchases/${wallpaperId}`, document: doc };
}

/**
 * Only the backend server (PROJECT_VAULT_ADDRESS) can create purchase records. This prevents users from bypassing the onchain SKR token burn by directly writing purchase documents. The backend API verifies the onchain burn succeeded before recording the purchase here. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUsersPurchases(userId: string, wallpaperId: string, data?: UsersPurchasesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`users/${userId}/purchases/${wallpaperId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting UsersPurchases: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: The owner can view their purchased wallpapers library. Backend server (PROJECT_VAULT_ADDRESS) can also read to verify purchases before allowing downloads.
   (Get Single Item)
 */
export async function getUsersPurchases(userId: string, wallpaperId: string): Promise<UsersPurchasesResponse | null> {
  try {
    const result = await get(`users/${userId}/purchases/${wallpaperId}`);
    return result as UsersPurchasesResponse | null;
  } catch (error) {
    console.error(`Error getting UsersPurchases: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single UsersPurchases document. (
  Read Operation Details: The owner can view their purchased wallpapers library. Backend server (PROJECT_VAULT_ADDRESS) can also read to verify purchases before allowing downloads.
  )
 */
export function subscribeUsersPurchases(callback: (data: UsersPurchasesResponse | null) => void, userId: string, wallpaperId: string): Promise<() => Promise<void>> {
  return subscribe(`users/${userId}/purchases/${wallpaperId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UsersPurchasesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to UsersPurchases at path '${`users/${userId}/purchases/${wallpaperId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many UsersPurchases items from collection users/${userId}/purchases
 
  Read Operation Details: The owner can view their purchased wallpapers library. Backend server (PROJECT_VAULT_ADDRESS) can also read to verify purchases before allowing downloads.
  
 */
export async function getManyUsersPurchases(userId: string, filter: string = ""): Promise<UsersPurchasesResponse[]> {
  try {
    const results = await get(`users/${userId}/purchases`, { prompt: filter });
    return Array.isArray(results) ? results as UsersPurchasesResponse[] : [];
  } catch (error) {
    console.error(`Error getting UsersPurchases collection at path 'users/${userId}/purchases': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UsersPurchases collection at users/${userId}/purchases
 
  Read Operation Details: The owner can view their purchased wallpapers library. Backend server (PROJECT_VAULT_ADDRESS) can also read to verify purchases before allowing downloads.
  
 */
export function subscribeManyUsersPurchases(callback: (data: UsersPurchasesResponse[]) => void, userId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`users/${userId}/purchases`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersPurchasesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UsersPurchases collection at path '${`users/${userId}/purchases`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all UsersPurchases items from collection users/${userId}/purchases
 
  Read Operation Details: The owner can view their purchased wallpapers library. Backend server (PROJECT_VAULT_ADDRESS) can also read to verify purchases before allowing downloads.
  
 */
export async function getAllUsersPurchases(userId: string, filter: string = ""): Promise<UsersPurchasesResponse[]> {
  try {
    const results = await get(`users/*/purchases`, { prompt: filter });
    return Array.isArray(results) ? results as UsersPurchasesResponse[] : [];
  } catch (error) {
    console.error(`Error getting UsersPurchases collection at path 'users/${userId}/purchases': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UsersPurchases collection at users/${userId}/purchases
 
  Read Operation Details: The owner can view their purchased wallpapers library. Backend server (PROJECT_VAULT_ADDRESS) can also read to verify purchases before allowing downloads.
  
 */
export function subscribeAllUsersPurchases(callback: (data: UsersPurchasesResponse[]) => void, userId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`users/*/purchases`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersPurchasesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UsersPurchases collection at path '${`users/*/purchases`}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Only the owner can remove a wallpaper from their library.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteUsersPurchases(userId: string, wallpaperId: string): Promise<boolean> {
  try {
    const succeeded = await set(`users/${userId}/purchases/${wallpaperId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting UsersPurchases: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for UsersPurchases for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteUsersPurchases(userId: string, wallpaperId: string): DocumentOperation {
  return { path: `users/${userId}/purchases/${wallpaperId}`, document: null };
}


/**
 * Tracks wallpapers a user has favorited for quick access. Each document represents one favorited wallpaper.
 */

export interface UsersFavoritesRequest {
  wallpaperId: string;
  owner: AddressType;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UsersFavoritesResponse {
  wallpaperId: string;
  owner: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a UsersFavorites operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUsersFavorites(userId: string, wallpaperId: string, data?: UsersFavoritesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `users/${userId}/favorites/${wallpaperId}`, document: doc };
}

/**
 * Authenticated users can favorite a wallpaper. The owner field must match the caller wallet address and is immutable. wallpaperId should reference a valid wallpaper from the wallpapers collection. createdAt is set once on creation (Unix timestamp seconds). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUsersFavorites(userId: string, wallpaperId: string, data?: UsersFavoritesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`users/${userId}/favorites/${wallpaperId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting UsersFavorites: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Only the owner can view their favorited wallpapers. Private per user.
   (Get Single Item)
 */
export async function getUsersFavorites(userId: string, wallpaperId: string): Promise<UsersFavoritesResponse | null> {
  try {
    const result = await get(`users/${userId}/favorites/${wallpaperId}`);
    return result as UsersFavoritesResponse | null;
  } catch (error) {
    console.error(`Error getting UsersFavorites: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single UsersFavorites document. (
  Read Operation Details: Only the owner can view their favorited wallpapers. Private per user.
  )
 */
export function subscribeUsersFavorites(callback: (data: UsersFavoritesResponse | null) => void, userId: string, wallpaperId: string): Promise<() => Promise<void>> {
  return subscribe(`users/${userId}/favorites/${wallpaperId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UsersFavoritesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to UsersFavorites at path '${`users/${userId}/favorites/${wallpaperId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many UsersFavorites items from collection users/${userId}/favorites
 
  Read Operation Details: Only the owner can view their favorited wallpapers. Private per user.
  
 */
export async function getManyUsersFavorites(userId: string, filter: string = ""): Promise<UsersFavoritesResponse[]> {
  try {
    const results = await get(`users/${userId}/favorites`, { prompt: filter });
    return Array.isArray(results) ? results as UsersFavoritesResponse[] : [];
  } catch (error) {
    console.error(`Error getting UsersFavorites collection at path 'users/${userId}/favorites': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UsersFavorites collection at users/${userId}/favorites
 
  Read Operation Details: Only the owner can view their favorited wallpapers. Private per user.
  
 */
export function subscribeManyUsersFavorites(callback: (data: UsersFavoritesResponse[]) => void, userId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`users/${userId}/favorites`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersFavoritesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UsersFavorites collection at path '${`users/${userId}/favorites`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all UsersFavorites items from collection users/${userId}/favorites
 
  Read Operation Details: Only the owner can view their favorited wallpapers. Private per user.
  
 */
export async function getAllUsersFavorites(userId: string, filter: string = ""): Promise<UsersFavoritesResponse[]> {
  try {
    const results = await get(`users/*/favorites`, { prompt: filter });
    return Array.isArray(results) ? results as UsersFavoritesResponse[] : [];
  } catch (error) {
    console.error(`Error getting UsersFavorites collection at path 'users/${userId}/favorites': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UsersFavorites collection at users/${userId}/favorites
 
  Read Operation Details: Only the owner can view their favorited wallpapers. Private per user.
  
 */
export function subscribeAllUsersFavorites(callback: (data: UsersFavoritesResponse[]) => void, userId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`users/*/favorites`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersFavoritesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UsersFavorites collection at path '${`users/*/favorites`}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Only the owner can unfavorite a wallpaper by deleting the record.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteUsersFavorites(userId: string, wallpaperId: string): Promise<boolean> {
  try {
    const succeeded = await set(`users/${userId}/favorites/${wallpaperId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting UsersFavorites: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for UsersFavorites for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteUsersFavorites(userId: string, wallpaperId: string): DocumentOperation {
  return { path: `users/${userId}/favorites/${wallpaperId}`, document: null };
}


/**
 * Tracks wallpapers a user has trashed (soft-deleted) for potential recovery or permanent removal. Each document represents one trashed wallpaper.
 */

export interface UsersTrashRequest {
  wallpaperId: string;
  owner: AddressType;
  deletedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UsersTrashResponse {
  wallpaperId: string;
  owner: string;
  deletedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a UsersTrash operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUsersTrash(userId: string, wallpaperId: string, data?: UsersTrashRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `users/${userId}/trash/${wallpaperId}`, document: doc };
}

/**
 * Authenticated users can move a wallpaper to trash. The owner field must match the caller wallet address and is immutable. wallpaperId should reference a valid wallpaper from the wallpapers collection. deletedAt is set once on creation (Unix timestamp seconds). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUsersTrash(userId: string, wallpaperId: string, data?: UsersTrashRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`users/${userId}/trash/${wallpaperId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting UsersTrash: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Only the owner can view their trashed wallpapers. Private per user.
   (Get Single Item)
 */
export async function getUsersTrash(userId: string, wallpaperId: string): Promise<UsersTrashResponse | null> {
  try {
    const result = await get(`users/${userId}/trash/${wallpaperId}`);
    return result as UsersTrashResponse | null;
  } catch (error) {
    console.error(`Error getting UsersTrash: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single UsersTrash document. (
  Read Operation Details: Only the owner can view their trashed wallpapers. Private per user.
  )
 */
export function subscribeUsersTrash(callback: (data: UsersTrashResponse | null) => void, userId: string, wallpaperId: string): Promise<() => Promise<void>> {
  return subscribe(`users/${userId}/trash/${wallpaperId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UsersTrashResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to UsersTrash at path '${`users/${userId}/trash/${wallpaperId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many UsersTrash items from collection users/${userId}/trash
 
  Read Operation Details: Only the owner can view their trashed wallpapers. Private per user.
  
 */
export async function getManyUsersTrash(userId: string, filter: string = ""): Promise<UsersTrashResponse[]> {
  try {
    const results = await get(`users/${userId}/trash`, { prompt: filter });
    return Array.isArray(results) ? results as UsersTrashResponse[] : [];
  } catch (error) {
    console.error(`Error getting UsersTrash collection at path 'users/${userId}/trash': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UsersTrash collection at users/${userId}/trash
 
  Read Operation Details: Only the owner can view their trashed wallpapers. Private per user.
  
 */
export function subscribeManyUsersTrash(callback: (data: UsersTrashResponse[]) => void, userId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`users/${userId}/trash`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersTrashResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UsersTrash collection at path '${`users/${userId}/trash`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all UsersTrash items from collection users/${userId}/trash
 
  Read Operation Details: Only the owner can view their trashed wallpapers. Private per user.
  
 */
export async function getAllUsersTrash(userId: string, filter: string = ""): Promise<UsersTrashResponse[]> {
  try {
    const results = await get(`users/*/trash`, { prompt: filter });
    return Array.isArray(results) ? results as UsersTrashResponse[] : [];
  } catch (error) {
    console.error(`Error getting UsersTrash collection at path 'users/${userId}/trash': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UsersTrash collection at users/${userId}/trash
 
  Read Operation Details: Only the owner can view their trashed wallpapers. Private per user.
  
 */
export function subscribeAllUsersTrash(callback: (data: UsersTrashResponse[]) => void, userId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`users/*/trash`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersTrashResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UsersTrash collection at path '${`users/*/trash`}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Only the owner can permanently remove a wallpaper from their trash.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteUsersTrash(userId: string, wallpaperId: string): Promise<boolean> {
  try {
    const succeeded = await set(`users/${userId}/trash/${wallpaperId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting UsersTrash: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for UsersTrash for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteUsersTrash(userId: string, wallpaperId: string): DocumentOperation {
  return { path: `users/${userId}/trash/${wallpaperId}`, document: null };
}

